// Album manifest contract — FROZEN by Anvil 2026-08-04 (slice 1a), verbatim
// from the live GET /a/:shareToken sandbox response. This mirrors that shape
// field-for-field.
//
// PARITY-CRITICAL:
//  - `pages[].layout` = REAL layout id from photobook_page_layouts.json
//    (pinned by `layoutLibraryVersion`).
//  - `pages[].slots[]` carry an explicit `slotIndex` — map fills BY slotIndex,
//    never by array position.
//  - `crop` is normalized [0,1] against the UPRIGHT original (scale-1/crop model).
//  - `look` (per page) + `coverLook` (top-level) are SERVER-RESOLVED effective
//    filter ids (final id, ready to apply). The client NEVER re-resolves scope.

/** Normalized crop rect against the upright original, fractions in [0,1]. */
export interface Crop {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Resolved effective filter id — the app's full PhotoFilterId enum. */
export type FilterId = "none" | "mono" | "noir" | "sepia" | "fade";

export interface PhotoRef {
  w: number; // upright display dims
  h: number;
  /** Only page-size derivatives — never full-res (PRD §5). Opaque URLs:
   *  1a backs both with the ~1600 presigned proxy; 1b swaps to webp 320/1280
   *  with zero shape change. */
  srcset: {
    thumb: string;
    page: string;
  };
  /** Optional alt; absent/empty => decorative (empty alt). */
  alt?: string;
}

export interface PageSlot {
  slotIndex: number;
  photoId: string;
  crop: Crop;
}

export interface ManifestPage {
  index: number;
  layout: string; // real layout id
  slots: PageSlot[]; // [] on blank pages
  look?: FilterId; // resolved effective per-page look
  blank?: boolean; // intentional padding page -> render background only
}

// --- Cover §6 wire (identical to the iOS cover editor's output) ---

export type CoverBackground =
  | { type: "photo"; photoId: string; crop: Crop; layout: string }
  | { type: "color"; colorId: string } // palette id OR #RRGGBB
  | { type: "pattern"; patternId: string };

export interface CoverTitlePlate {
  enabled: boolean;
  colorId: string;
}

export interface CoverTitle {
  text: string;
  fontId: string; // "fraunces" | "bebas" | …
  weight: number;
  sizePt: number; // cover points (trim = 210mm ≈ 595.28pt)
  position: { x: number; y: number }; // normalized CENTER of title block vs trim
  align: "left" | "center" | "right";
  color: string; // "auto" | palette id | #RRGGBB
  shadow: boolean;
  plate: CoverTitlePlate | null;
}

export interface CoverBlock {
  background: CoverBackground;
  title: CoverTitle;
  showTitle: boolean;
}

export interface AlbumManifest {
  id: string; // the shareToken (opaque)
  title: string;
  subtitle: string | null;
  sender: { name: string };
  cover: CoverBlock;
  coverLook: FilterId; // resolved cover-photo look (top-level)
  pageCount: number;
  layoutLibraryVersion: string; // pin vs the synced layout library
  pages: ManifestPage[];
  photos: Record<string, PhotoRef>;
  joinCode: string; // 6-char, contribute-only
  print: { available: boolean; checkoutUrl: string | null };
  permissions: { download: boolean; comment: boolean; reshare: boolean };
  /** Opaque equality-only staleness marker — refetch when it changes; never
   *  order-compare. */
  version?: string;
  updatedAt?: string;
}

/** A page is "content" if it isn't padding and has at least one slot pointing at
 *  a real photo. */
export function pageHasContent(
  page: ManifestPage,
  photos: Record<string, PhotoRef>,
): boolean {
  if (page.blank) return false;
  return page.slots.some((s) => s.photoId && photos[s.photoId] != null);
}

/** Display-only: drop blank padding + zero-photo pages so the web reader shows a
 *  clean book. Blank pages exist to satisfy the PRINT 24-page minimum (Prodigi's
 *  smallest hardcover); the web preview has no such floor. pageCount is
 *  re-derived so spread pairing + "N / total" follow the filtered set. The
 *  cover-open INSIDE board is a synthetic sentinel, not a manifest page, so it's
 *  untouched. The underlying manifest/print book is unchanged. */
export function hideEmptyPages(m: AlbumManifest): AlbumManifest {
  const pages = m.pages.filter((p) => pageHasContent(p, m.photos));
  if (pages.length === m.pages.length) return m; // nothing to hide
  return { ...m, pages, pageCount: pages.length };
}
